/**
 * /teachers — replaces the old passcode-entry. Now a login form that gates
 * access to /teachers/dashboard, /teachers/profile, /teachers/lessons, /teachers/resources.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useTeacherAuth } from "../TeacherAuthContext";

export default function TeacherLogin() {
  const { setSession, status } = useTeacherAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If already logged in, jump to dashboard
  React.useEffect(() => {
    if (status === "auth") navigate("/teachers/dashboard", { replace: true });
  }, [status, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await api.teacherLogin(email.trim().toLowerCase(), password);
      setSession(data.token, data.teacher);
      navigate("/teachers/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Could not sign in");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6"
      data-testid="teachers-page"
    >
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          Teachers
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          Welcome, teacher.
        </h1>
        <p className="mt-4 text-base text-[#1C1C1C]/70 leading-relaxed">
          Sign in with the email address and password given to you by the school administrator.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <Field label="Email">
            <input
              data-testid="teacher-login-email"
              autoFocus
              required
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Password">
            <input
              data-testid="teacher-login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </Field>
          <div className="text-right -mt-1">
            <Link
              to="/teachers/forgot"
              className="text-[11px] tracking-[0.22em] uppercase text-[#1C1C1C]/55 hover:text-[#1C1C1C]"
              data-testid="teacher-login-forgot-link"
            >
              Forgot password?
            </Link>
          </div>
          {error && (
            <div className="text-sm text-[#B83A3A]" data-testid="teacher-login-error">{error}</div>
          )}
          <button
            data-testid="teacher-login-submit"
            type="submit"
            disabled={loading}
            className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <style>{`.input{width:100%;padding:.75rem 1rem;background:white;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:1rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4)}`}</style>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-2">{label}</div>
      {children}
    </label>
  );
}
